import { useState, useEffect, useCallback } from "react";
import { API, useAuth } from "../App";
import QMSLayout from "../components/QMSLayout";
import axios from "axios";
import { useTranslation } from "react-i18next";
import {
  Shield, Loader2, Plus, Lock, Key, Users, ArrowLeft,
  ChevronRight, CheckCircle2, XCircle, AlertTriangle, Trash2,
  Globe, FileText
} from "lucide-react";

const ax = axios.create({ withCredentials: true });

export default function SSOProvidersPage() {
  const { t } = useTranslation('platform');
  const { user } = useAuth();
  const [tab, setTab] = useState("providers");
  const [providers, setProviders] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewProvider, setShowNewProvider] = useState(false);
  const [showNewMapping, setShowNewMapping] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);

  const loadProviders = useCallback(async () => {
    try {
      const res = await ax.get(`${API}/sso-connectors/providers`);
      setProviders(res.data.providers || []);
    } catch {}
  }, []);

  const loadMappings = useCallback(async () => {
    try {
      const res = await ax.get(`${API}/sso-connectors/role-mappings`);
      setMappings(res.data.mappings || []);
    } catch {}
  }, []);

  const loadLogs = useCallback(async () => {
    try {
      const res = await ax.get(`${API}/sso-connectors/audit-logs`);
      setAuditLogs(res.data.logs || []);
    } catch {}
  }, []);

  useEffect(() => {
    Promise.all([loadProviders(), loadMappings(), loadLogs()]).then(() => setLoading(false));
  }, [loadProviders, loadMappings, loadLogs]);

  const createProvider = async (data) => {
    try {
      await ax.post(`${API}/sso-connectors/providers`, data);
      setShowNewProvider(false);
      loadProviders();
      loadLogs();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to create provider");
    }
  };

  const createMapping = async (data) => {
    try {
      await ax.post(`${API}/sso-connectors/role-mappings`, data);
      setShowNewMapping(false);
      loadMappings();
      loadLogs();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to create mapping");
    }
  };

  const deleteMapping = async (mappingId) => {
    if (!window.confirm(t('ssoProviders.deleteConfirm'))) return;
    try {
      await ax.delete(`${API}/sso-connectors/role-mappings/${mappingId}`);
      loadMappings();
      loadLogs();
    } catch {}
  };

  if (loading) {
    return (
      <QMSLayout title={t('platform:ssoProviders.title')}>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-[#36A9E1] animate-spin" />
        </div>
      </QMSLayout>
    );
  }

  // Provider Detail
  if (selectedProvider) {
    const providerMappings = mappings.filter(m => m.provider_id === selectedProvider.provider_id);
    return (
      <QMSLayout title={t('platform:ssoProviders.title')}>
        <div className="space-y-4" data-testid="sso-provider-detail">
          <button onClick={() => setSelectedProvider(null)} className="flex items-center gap-2 text-[#8B95A5] hover:text-white text-sm" data-testid="back-from-provider">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="bg-[#181B23] rounded-lg p-5 border border-[#2A2D38]">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-white font-bold text-lg">{selectedProvider.name}</h2>
              <div className="flex items-center gap-2">
                <span className="text-xs bg-[#1F222D] text-[#8B95A5] px-2 py-1 rounded">{selectedProvider.provider_type.toUpperCase()}</span>
                <span className={`px-2 py-1 rounded text-xs ${selectedProvider.active ? "bg-emerald-400/10 text-emerald-400" : "bg-red-400/10 text-red-400"}`}>
                  {selectedProvider.active ? t('ssoProviders.active') : t('ssoProviders.inactive')}
                </span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm mb-4">
              <div><span className="text-[#8B95A5]">Issuer:</span> <span className="text-white ml-2">{selectedProvider.issuer || "—"}</span></div>
              <div><span className="text-[#8B95A5]">Client ID:</span> <span className="text-white ml-2">{selectedProvider.client_id || "—"}</span></div>
              <div><span className="text-[#8B95A5]">Redirect URI:</span> <span className="text-white ml-2">{selectedProvider.redirect_uri || "—"}</span></div>
              <div><span className="text-[#8B95A5]">Scopes:</span> <span className="text-white ml-2">{(selectedProvider.scopes || []).join(", ")}</span></div>
            </div>

            <h3 className="text-white font-semibold text-sm mb-2">Role Mappings ({providerMappings.length})</h3>
            {providerMappings.length === 0 ? (
              <p className="text-[#8B95A5] text-sm">No role mappings configured.</p>
            ) : (
              <div className="space-y-1">
                {providerMappings.map(m => (
                  <div key={m.mapping_id} className="flex items-center justify-between bg-[#0D0F14] rounded p-3">
                    <div className="text-sm">
                      <span className="text-[#36A9E1]">{m.external_group}</span>
                      <span className="text-[#8B95A5] mx-2">-&gt;</span>
                      <span className="text-white font-medium">{m.internal_role}</span>
                    </div>
                    <button onClick={() => deleteMapping(m.mapping_id)} className="text-red-400 hover:text-red-300" data-testid={`delete-mapping-${m.mapping_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </QMSLayout>
    );
  }

  const tabs = [
    { id: "providers", label: "Providers", icon: Globe },
    { id: "mappings", label: "Role Mappings", icon: Users },
    { id: "logs", label: "Audit Logs", icon: FileText },
  ];

  return (
    <QMSLayout title={t('platform:ssoProviders.title')}>
      <div className="space-y-4" data-testid="sso-providers-page">
        <div className="flex items-center justify-between">
          <div className="flex gap-1 bg-[#181B23] rounded-lg p-1" data-testid="sso-tabs">
            {tabs.map(tabItem => {
              const Icon = tabItem.icon;
              return (
                <button key={tabItem.id} onClick={() => setTab(tabItem.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded text-sm ${tab === tabItem.id ? "bg-[#36A9E1]/10 text-[#36A9E1]" : "text-[#8B95A5] hover:text-white"}`}
                  data-testid={`tab-${tabItem.id}`}
                ><Icon className="w-4 h-4" />{tabItem.label}</button>
              );
            })}
          </div>
          <div className="flex gap-2">
            {tab === "providers" && user?.role === "admin" && (
              <button onClick={() => setShowNewProvider(true)} className="px-3 py-2 rounded bg-[#36A9E1] text-white text-sm flex items-center gap-2 hover:bg-[#2B8BC2]" data-testid="new-provider-btn">
                <Plus className="w-4 h-4" /> Add Provider
              </button>
            )}
            {tab === "mappings" && user?.role === "admin" && (
              <button onClick={() => setShowNewMapping(true)} className="px-3 py-2 rounded bg-[#36A9E1] text-white text-sm flex items-center gap-2 hover:bg-[#2B8BC2]" data-testid="new-mapping-btn">
                <Plus className="w-4 h-4" /> Add Mapping
              </button>
            )}
          </div>
        </div>

        {/* Providers */}
        {tab === "providers" && (
          <div className="space-y-2" data-testid="providers-list">
            {providers.length === 0 ? (
              <div className="bg-[#181B23] rounded-lg p-8 text-center text-[#8B95A5]">
                <Shield className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p>No SSO providers configured. Admin can add one.</p>
              </div>
            ) : providers.map(p => (
              <div key={p.provider_id} onClick={() => setSelectedProvider(p)}
                className="bg-[#181B23] rounded-lg p-4 border border-[#2A2D38] hover:border-[#36A9E1]/30 cursor-pointer"
                data-testid={`provider-${p.provider_id}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-white font-medium text-sm">{p.name}</div>
                    <div className="text-xs text-[#8B95A5] mt-1">{p.provider_type.toUpperCase()} &middot; {p.issuer || "No issuer"}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded text-xs ${p.active ? "bg-emerald-400/10 text-emerald-400" : "bg-red-400/10 text-red-400"}`}>
                      {p.active ? t('ssoProviders.active') : t('ssoProviders.inactive')}
                    </span>
                    <ChevronRight className="w-4 h-4 text-[#8B95A5]" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Role Mappings */}
        {tab === "mappings" && (
          <div className="space-y-2" data-testid="mappings-list">
            {mappings.length === 0 ? (
              <div className="bg-[#181B23] rounded-lg p-8 text-center text-[#8B95A5]">
                <Users className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p>No role mappings configured.</p>
              </div>
            ) : mappings.map(m => (
              <div key={m.mapping_id} className="bg-[#181B23] rounded-lg p-4 border border-[#2A2D38]" data-testid={`mapping-${m.mapping_id}`}>
                <div className="flex items-center justify-between">
                  <div className="text-sm">
                    <span className="text-[#36A9E1] font-medium">{m.external_group}</span>
                    <span className="text-[#8B95A5] mx-3">-&gt;</span>
                    <span className="text-white font-medium">{m.internal_role}</span>
                    <span className="text-xs text-[#8B95A5] ml-3">({m.provider_id})</span>
                  </div>
                  {user?.role === "admin" && (
                    <button onClick={() => deleteMapping(m.mapping_id)} className="text-red-400 hover:text-red-300 p-1">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Audit Logs */}
        {tab === "logs" && (
          <div className="space-y-2" data-testid="sso-audit-logs">
            {auditLogs.length === 0 ? (
              <div className="bg-[#181B23] rounded-lg p-8 text-center text-[#8B95A5]">No SSO audit logs.</div>
            ) : auditLogs.map(l => (
              <div key={l.log_id} className="bg-[#181B23] rounded-lg p-3 border border-[#2A2D38] text-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-[#36A9E1]">{l.action}</span>
                    <span className="text-[#8B95A5] ml-2">{l.details}</span>
                  </div>
                  <span className="text-xs text-[#8B95A5]">{new Date(l.timestamp).toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modals */}
        {showNewProvider && <NewProviderModal onSubmit={createProvider} onClose={() => setShowNewProvider(false)} />}
        {showNewMapping && <NewMappingModal providers={providers} onSubmit={createMapping} onClose={() => setShowNewMapping(false)} />}
      </div>
    </QMSLayout>
  );
}

function NewProviderModal({ onSubmit, onClose }) {
  const { t } = useTranslation(['platform', 'common']);
  const [name, setName] = useState("");
  const [providerType, setProviderType] = useState("oidc");
  const [issuer, setIssuer] = useState("");
  const [clientId, setClientId] = useState("");
  const [redirectUri, setRedirectUri] = useState("");

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="new-provider-modal">
      <div className="bg-[#181B23] rounded-xl border border-[#2A2D38] w-full max-w-lg p-6">
        <h2 className="text-white font-bold text-lg mb-4">Add SSO Provider</h2>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-[#8B95A5] block mb-1">Name</label>
            <input value={name} onChange={e => setName(e.target.value)} className="w-full bg-[#0D0F14] border border-[#2A2D38] rounded px-3 py-2 text-white text-sm" data-testid="provider-name-input" />
          </div>
          <div>
            <label className="text-xs text-[#8B95A5] block mb-1">Type</label>
            <select value={providerType} onChange={e => setProviderType(e.target.value)} className="w-full bg-[#0D0F14] border border-[#2A2D38] rounded px-3 py-2 text-white text-sm" data-testid="provider-type-select">
              <option value="oidc">OIDC</option>
              <option value="saml">SAML</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-[#8B95A5] block mb-1">Issuer URL</label>
            <input value={issuer} onChange={e => setIssuer(e.target.value)} placeholder="https://idp.example.com" className="w-full bg-[#0D0F14] border border-[#2A2D38] rounded px-3 py-2 text-white text-sm" data-testid="provider-issuer-input" />
          </div>
          <div>
            <label className="text-xs text-[#8B95A5] block mb-1">Client ID</label>
            <input value={clientId} onChange={e => setClientId(e.target.value)} className="w-full bg-[#0D0F14] border border-[#2A2D38] rounded px-3 py-2 text-white text-sm" data-testid="provider-client-id-input" />
          </div>
          <div>
            <label className="text-xs text-[#8B95A5] block mb-1">Redirect URI</label>
            <input value={redirectUri} onChange={e => setRedirectUri(e.target.value)} className="w-full bg-[#0D0F14] border border-[#2A2D38] rounded px-3 py-2 text-white text-sm" data-testid="provider-redirect-input" />
          </div>
        </div>
        <div className="flex gap-3 mt-5 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded bg-[#1F222D] text-[#8B95A5] hover:bg-[#2A2D38] text-sm">{t('common:actions.cancel')}</button>
          <button onClick={() => onSubmit({ name, provider_type: providerType, issuer, client_id: clientId, redirect_uri: redirectUri })}
            disabled={!name}
            className="px-4 py-2 rounded bg-[#36A9E1] text-white text-sm hover:bg-[#2B8BC2] disabled:opacity-40"
            data-testid="create-provider-submit"
          >Create Provider</button>
        </div>
      </div>
    </div>
  );
}

function NewMappingModal({ providers, onSubmit, onClose }) {
  const { t } = useTranslation(['platform', 'common']);
  const [providerId, setProviderId] = useState(providers[0]?.provider_id || "");
  const [externalGroup, setExternalGroup] = useState("");
  const [internalRole, setInternalRole] = useState("client");

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="new-mapping-modal">
      <div className="bg-[#181B23] rounded-xl border border-[#2A2D38] w-full max-w-lg p-6">
        <h2 className="text-white font-bold text-lg mb-4">Add Role Mapping</h2>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-[#8B95A5] block mb-1">Provider</label>
            <select value={providerId} onChange={e => setProviderId(e.target.value)} className="w-full bg-[#0D0F14] border border-[#2A2D38] rounded px-3 py-2 text-white text-sm" data-testid="mapping-provider-select">
              {providers.map(p => <option key={p.provider_id} value={p.provider_id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-[#8B95A5] block mb-1">External Group</label>
            <input value={externalGroup} onChange={e => setExternalGroup(e.target.value)} placeholder="e.g. QMS_Admins" className="w-full bg-[#0D0F14] border border-[#2A2D38] rounded px-3 py-2 text-white text-sm" data-testid="mapping-group-input" />
          </div>
          <div>
            <label className="text-xs text-[#8B95A5] block mb-1">Internal Role</label>
            <select value={internalRole} onChange={e => setInternalRole(e.target.value)} className="w-full bg-[#0D0F14] border border-[#2A2D38] rounded px-3 py-2 text-white text-sm" data-testid="mapping-role-select">
              <option value="admin">Admin</option>
              <option value="auditor">Auditor</option>
              <option value="client">Client</option>
              <option value="reader">Reader</option>
            </select>
          </div>
        </div>
        <div className="flex gap-3 mt-5 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded bg-[#1F222D] text-[#8B95A5] hover:bg-[#2A2D38] text-sm">{t('common:actions.cancel')}</button>
          <button onClick={() => onSubmit({ provider_id: providerId, external_group: externalGroup, internal_role: internalRole })}
            disabled={!externalGroup || !providerId}
            className="px-4 py-2 rounded bg-[#36A9E1] text-white text-sm hover:bg-[#2B8BC2] disabled:opacity-40"
            data-testid="create-mapping-submit"
          >Create Mapping</button>
        </div>
      </div>
    </div>
  );
}
